/**
 * AI Tips Service
 * 
 * Frontend service for managing AI-generated cooking tips
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

class AiTipsService {
    constructor() {
        this.tipsCache = new Map(); // Cache tips by session ID
    }

    /**
     * Generate AI tips for a session
     * @param {string} sessionId - Session ID
     * @param {string} token - Firebase auth token
     * @returns {Promise<Object>} Generated tips and metadata
     */
    async generateTips(sessionId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/generate-tips`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to generate tips');
            }

            const result = await response.json();
            
            // Cache the tips
            if (result.tips) {
                this.tipsCache.set(sessionId, result.tips);
            }
            
            return result;
        } catch (error) {
            console.error('Error generating tips:', error);
            throw error;
        }
    }

    /**
     * Get tips for a specific step
     * @param {string} sessionId - Session ID
     * @param {number} stepIndex - Step index
     * @param {string} token - Firebase auth token
     * @returns {Promise<Array>} Tips for the step
     */
    async getTipsForStep(sessionId, stepIndex, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/tips/${stepIndex}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to get tips');
            }

            const result = await response.json();
            return result.tips || [];
        } catch (error) {
            console.error('Error getting tips for step:', error);
            // Return empty array on error - don't block the session
            return [];
        }
    }

    /**
     * Submit feedback for a tip
     * @param {string} sessionId - Session ID
     * @param {string} tipId - Tip ID
     * @param {boolean} helpful - Whether the tip was helpful
     * @param {string} token - Firebase auth token
     * @returns {Promise<void>}
     */
    async submitTipFeedback(sessionId, tipId, helpful, token) {
        try {
            const feedback = helpful ? 'helpful' : 'not_helpful';
            
            const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/tips/${tipId}/feedback`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ feedback })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to submit feedback');
            }

            return await response.json();
        } catch (error) {
            console.error('Error submitting tip feedback:', error);
            throw error;
        }
    }

    /**
     * Get cached tips for a session
     * @param {string} sessionId - Session ID
     * @returns {Array|null} Cached tips or null
     */
    getCachedTips(sessionId) {
        return this.tipsCache.get(sessionId) || null;
    }

    /**
     * Clear cache for a session
     * @param {string} sessionId - Session ID
     */
    clearCache(sessionId) {
        this.tipsCache.delete(sessionId);
    }

    /**
     * Filter tips by step index from cached tips
     * @param {string} sessionId - Session ID
     * @param {number} stepIndex - Step index
     * @returns {Array} Tips for the step
     */
    getStepTipsFromCache(sessionId, stepIndex) {
        const tips = this.getCachedTips(sessionId);
        if (!tips) return [];
        return tips.filter(tip => tip.stepIndex === stepIndex);
    }

    /**
     * Get priority badge info
     * @param {string} priority - Priority level
     * @returns {Object} Priority display info
     */
    getPriorityInfo(priority) {
        const priorities = {
            'critical': { name: 'Critical', color: '#dc2626', icon: '⚠️' },
            'helpful': { name: 'Helpful', color: '#2563eb', icon: '💡' },
            'informational': { name: 'Info', color: '#059669', icon: 'ℹ️' }
        };
        return priorities[priority] || priorities.helpful;
    }

    /**
     * Get type icon
     * @param {string} type - Tip type
     * @returns {string} Type icon
     */
    getTypeIcon(type) {
        const types = {
            'timing': '⏱️',
            'technique': '👨‍🍳',
            'ingredient': '🥘',
            'safety': '⚠️',
            'visual_check': '👁️',
            'general': '💡'
        };
        return types[type] || types.general;
    }
}

// Create singleton instance
const aiTipsService = new AiTipsService();

export default aiTipsService;

